import type { MouseEvent } from "react";

import { ButtonIcon } from "@backstage/ui";
import { RiAddLine, RiSubtractLine } from "@remixicon/react";

import {
    FONT_SIZE_MARKS,
    FONT_SIZE_MAX,
    FONT_SIZE_MIN,
    formatSizeText,
} from "../../constants";
import styles from "./CodeTextSize.module.css";

/**
 * Props for the {@link CodeTextSize} component.
 *
 * @public
 */
interface CodeTextSizeProps {
    /** The current text size as a percentage value (e.g. `100` for 100%). Must be one of the {@link FONT_SIZE_MARKS} values. */
    size: number;
    /** Callback invoked with the snap-to-nearest mark value when the slider changes. */
    onSizeCommitted: (event: unknown, value: number | number[]) => void;
    /** Callback invoked when the decrease button is pressed. */
    onDecrease: (event: MouseEvent) => void;
    /** Callback invoked when the increase button is pressed. */
    onIncrease: (event: MouseEvent) => void;
}

/**
 * A composite text-size control containing a labeled slider with decrease and increase buttons.
 *
 * Displays the current size as a percentage string via {@link formatSizeText}. The slider
 * snaps to the nearest {@link FONT_SIZE_MARKS} value on change. The decrease button is disabled
 * at {@link FONT_SIZE_MIN} and the increase button is disabled at {@link FONT_SIZE_MAX}.
 *
 * @param props - See {@link CodeTextSizeProps}.
 * @public
 */
export const CodeTextSize = ({
    size,
    onSizeCommitted,
    onDecrease,
    onIncrease,
}: CodeTextSizeProps) => {
    return (
        <div className={styles.row}>
            <div className={styles.labelRow}>
                <span className={styles.label}>Text size</span>
                <span className={styles.sliderValue}>
                    {formatSizeText(size)}
                </span>
            </div>
            <div className={styles.controls}>
                <ButtonIcon
                    size="small"
                    variant="tertiary"
                    isDisabled={size === FONT_SIZE_MIN}
                    onPress={e => onDecrease(e as unknown as MouseEvent)}
                    icon={<RiSubtractLine size={16} />}
                    aria-label="Decrease code text size"
                />
                <input
                    type="range"
                    className={styles.slider}
                    value={size}
                    min={FONT_SIZE_MIN}
                    max={FONT_SIZE_MAX}
                    onChange={e => {
                        const snapped = FONT_SIZE_MARKS.map(
                            m => m.value
                        ).reduce((prev, curr) =>
                            Math.abs(curr - Number(e.target.value)) <
                            Math.abs(prev - Number(e.target.value))
                                ? curr
                                : prev
                        );
                        onSizeCommitted(e, snapped);
                    }}
                    aria-label="Code text size"
                    aria-valuetext={formatSizeText(size)}
                />
                <ButtonIcon
                    size="small"
                    variant="tertiary"
                    isDisabled={size === FONT_SIZE_MAX}
                    onPress={e => onIncrease(e as unknown as MouseEvent)}
                    icon={<RiAddLine size={16} />}
                    aria-label="Increase code text size"
                />
            </div>
        </div>
    );
};
